import React from 'react'
import PropTypes from 'prop-types'
import { isWithinDateRange } from 'utils/dateHelpers'
import { LinkDescriptionTitle } from 'utils/highschoolHelpers'
import { formatPacingDates } from './helpers'
import {
  Container,
  PacingPill,
  Section,
  SectionHeader,
  StaticDescriptionLinkContainer,
  StaticLinksPanelContainer,
  StyledHairline,
  TileButton,
  TileGrid,
  TileImage,
  TileTitle,
} from './component.styles'
import { PROGRAM_TRAINING_TITLE } from './constants'

const tileShape = {
  endDate: PropTypes.string,
  image: PropTypes.string,
  path: PropTypes.string,
  startDate: PropTypes.string,
  title: PropTypes.string,
}

function PacingDatePill({ endDate, startDate }) {
  const label = formatPacingDates(startDate, endDate)
  if (!label) return null

  const isCurrent = isWithinDateRange(startDate, endDate)

  return (
    <PacingPill
      $isCurrent={isCurrent}
      data-pacing-state={isCurrent ? 'current' : 'outside'}
      data-testid="pacing-pill"
    >
      {label}
    </PacingPill>
  )
}

PacingDatePill.propTypes = {
  endDate: PropTypes.string,
  startDate: PropTypes.string,
}

function DashboardTile({
  endDate,
  image,
  navigationEvent,
  path,
  startDate,
  title,
}) {
  const hasPacing = Boolean(startDate && endDate)

  return (
    <TileButton
      onClick={() => {
        if (path && navigationEvent) navigationEvent(path)
      }}
      type="button"
    >
      <TileTitle>{title}</TileTitle>
      {hasPacing && <PacingDatePill endDate={endDate} startDate={startDate} />}
      {image && <TileImage alt="" src={image} />}
    </TileButton>
  )
}

DashboardTile.propTypes = {
  ...tileShape,
  navigationEvent: PropTypes.func,
}

function StaticDescriptionLink({ description, navigationEvent, title, path }) {
  return (
    <StaticDescriptionLinkContainer onClick={() => navigationEvent(path)}>
      <h4>{title}</h4>
      <p>{description}</p>
    </StaticDescriptionLinkContainer>
  )
}

StaticDescriptionLink.propTypes = {
  description: PropTypes.string,
  navigationEvent: PropTypes.func,
  path: PropTypes.string,
  title: PropTypes.string,
}

function StaticLinksPanel({ navigationEvent, staticLinks }) {
  return (
    <StaticLinksPanelContainer>
      {staticLinks?.map((link, index) => (
        <StaticDescriptionLink
          description={link?.description}
          key={index}
          navigationEvent={navigationEvent}
          path={link?.path}
          title={link?.title}
        />
      ))}
    </StaticLinksPanelContainer>
  )
}

StaticLinksPanel.propTypes = {
  navigationEvent: PropTypes.func,
  staticLinks: PropTypes.arrayOf(LinkDescriptionTitle),
}

function Component({
  navigationEvent,
  pathwayTiles,
  staticLinks,
  trainingTile,
}) {
  const training = {
    title: PROGRAM_TRAINING_TITLE,
    ...trainingTile,
  }

  return (
    <Container>
      <Section>
        <SectionHeader>Training</SectionHeader>
        <TileGrid>
          <DashboardTile {...training} navigationEvent={navigationEvent} />
        </TileGrid>
      </Section>
      <Section>
        <SectionHeader>Pathways</SectionHeader>
        <TileGrid>
          {pathwayTiles?.map(tile => (
            <DashboardTile
              key={tile?.path || tile?.title}
              {...tile}
              navigationEvent={navigationEvent}
            />
          ))}
        </TileGrid>
      </Section>
      <StyledHairline />
      <StaticLinksPanel
        navigationEvent={navigationEvent}
        staticLinks={staticLinks}
      />
    </Container>
  )
}

Component.propTypes = {
  navigationEvent: PropTypes.func,
  pathwayTiles: PropTypes.arrayOf(PropTypes.shape(tileShape)),
  staticLinks: PropTypes.arrayOf(LinkDescriptionTitle),
  trainingTile: PropTypes.shape(tileShape),
}

Component.displayName = 'InstanceView'

export default Component
